import { File06, Lock01 } from '@untitled-ui/icons-react/build/cjs';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { BULK_SELECTION_INITIAL_STATE } from 'src/app/Orders/order.const';
import BulkActionBar from 'src/components/BulkActionBar';
import CardCmp from 'src/components/CardCmp';
import CheckBox from 'src/components/CheckBox';
import CustomPagination from 'src/components/CustomPagination';
import DeleteModal from 'src/components/DeleteModal';
import NotFoundUI from 'src/components/NotFoundUI';
import TableCmp from 'src/components/TableCmp';
import { ROUTES } from 'src/constants/routes';
import { removeLegDocument, setLegDocumentsState, orderDetailsData, orderDetailsLoading, selectCurrentLegData, selectLegDocumentsState, selectOrderLegActiveTab } from 'src/redux/orders.slice';
import { deleteLegDocument } from 'src/services/OrderService';
import WalToast from 'src/utils/WalToast';

import LegDocumentsLoaderRow from './LegDocumentsLoaderRow';
import LegDocumentsRow from './LegDocumentsRow';
import DocumentPreviewModal from './modals/DocumentPreviewModal';

const DUMMY_DOCUMENTS = [
    { id: 1, fileName: 'Bill_of_Lading_WAL-TC3B-1909.pdf', legShowId: '#WAL-TC3B-1909', documentType: 'BOL', createdAt: '2026-05-18T10:00:00Z', uploadedByType: 'system' },
    { id: 2, fileName: 'Customer_Invoice_WAL-TC3B-1909.pdf', legShowId: '#WAL-TC3B-1909', documentType: 'CUSTOMER_INVOICE', createdAt: '2026-05-20T08:30:00Z', uploadedByType: 'system' },
    {
        id: 3,
        fileName: 'Proof_of_Delivery_signed.pdf',
        legShowId: '#WAL-TC3B-1909',
        documentType: 'POD',
        createdAt: '2026-05-21T14:15:00Z',
        uploadedByType: 'user',
        uploadedByUser: { name: 'Carrier Agent' },
    },
    { id: 4, fileName: 'Customs_Declaration_Form.pdf', legShowId: '#WAL-TC3B-1909', documentType: 'CUSTOMS_DOCUMENT', createdAt: '2026-05-22T09:45:00Z', uploadedByType: 'system' },
];

interface ILegDocumentsSectionProps {
    isReadOnly?: boolean;
}

const LegDocumentsSection = ({ isReadOnly = false }: ILegDocumentsSectionProps) => {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const loading = useSelector(orderDetailsLoading);
    const order = useSelector(orderDetailsData);
    const currentLegData = useSelector(selectCurrentLegData);
    const orderLegActiveTab = useSelector(selectOrderLegActiveTab);
    const { viewDocument, deleteDocumentId, isDeleting } = useSelector(selectLegDocumentsState);

    const [currentPage, setCurrentPage] = useState(1);
    const recordsPerPage = 5;

    const [bulkSelection, setBulkSelection] = useState<{ isOpen: boolean; data: number[] }>(BULK_SELECTION_INITIAL_STATE);
    const [showAllSelected, setShowAllSelected] = useState(false);
    const selectedIds = bulkSelection.data;

    const setSelectedIds = useCallback((ids: number[]) => {
        if (ids.length === 0) setShowAllSelected(false);
        setBulkSelection({ isOpen: ids.length > 0, data: ids });
    }, []);

    const handleToggleSelectRow = useCallback((id: number) => {
        setBulkSelection(({ data }) => {
            const next = data.includes(id) ? data.filter((i) => i !== id) : [...data, id];
            if (next.length === 0) setShowAllSelected(false);

            return { isOpen: next.length > 0, data: next };
        });
    }, []);

    const filteredDocuments = useMemo(() => {
        const parseDocs = (raw: any): any[] => {
            if (Array.isArray(raw)) return raw;

            if (typeof raw === 'string') {
                try {
                    return JSON.parse(raw);
                } catch {
                    return [];
                }
            }

            return [];
        };

        const isMaster = !!orderLegActiveTab?.isMaster;
        const orderLegs: any[] = order?.orderLegs ?? [];
        const isSingleLegOrder = orderLegs.length <= 1;

        // Order-level paperwork (orderLegId NULL — the master BOL, customer confirmation)
        // belongs to the whole route: it shows on the master tab, or on the lone leg tab
        // when the order has a single leg and therefore no master tab.
        const orderLevelDocs = parseDocs(order?.orderDocuments).map((doc: any) => ({
            ...doc,
            legShowId: order?.orderId ? `#${order.orderId}` : '',
            isOrderLevelDocument: true,
        }));

        const legDocsFor = (leg: any): any[] => {
            // Prefer currentLegData for the selected leg so optimistic upload/delete reflects
            // immediately without waiting for a full order re-fetch.
            const source = currentLegData && `${currentLegData?.id}` === `${leg?.id}` ? currentLegData : leg;
            const legShowId = leg?.legId ? `#${leg.legId}` : '';

            return (
                parseDocs(source?.documents)
                    // Run-resolved docs are an anchor leg's rows mirrored onto the run-last leg —
                    // skip them in lists or they'd show twice.
                    .filter((doc: any) => !doc?.isRunResolved)
                    .map((doc: any) => ({ ...doc, legShowId, orderLegId: leg?.id }))
            );
        };

        let docs: any[];

        if (isMaster) {
            // Master tab: order-level docs + every leg's own docs, each labeled by its leg
            docs = [...orderLevelDocs, ...orderLegs.flatMap(legDocsFor)];
        } else {
            const activeLeg = orderLegs.find((leg: any) => `${leg?.id}` === orderLegActiveTab?.tabValue) || currentLegData;
            docs = [...(isSingleLegOrder ? orderLevelDocs : []), ...(activeLeg ? legDocsFor(activeLeg) : [])];
        }

        return docs.sort((a: any, b: any) => new Date(b?.updatedAt).getTime() - new Date(a?.updatedAt).getTime());
    }, [order?.orderDocuments, order?.orderLegs, order?.orderId, currentLegData, orderLegActiveTab]);

    const allDocumentIds = useMemo(() => filteredDocuments.map((doc: any) => doc.id), [filteredDocuments]);

    const displayDocuments = useMemo(
        () => (showAllSelected ? filteredDocuments.filter((doc: any) => selectedIds.includes(doc.id)) : filteredDocuments),
        [showAllSelected, filteredDocuments, selectedIds]
    );

    const paginatedDocuments = useMemo(() => displayDocuments.slice((currentPage - 1) * recordsPerPage, currentPage * recordsPerPage), [displayDocuments, currentPage, recordsPerPage]);

    const headCells = useMemo(
        () => [
            {
                id: 'checkbox',
                name: (
                    <CheckBox
                        id="selectAllDocs"
                        checked={paginatedDocuments.length > 0 && paginatedDocuments.every((doc: any) => selectedIds.includes(doc.id))}
                        onChangeFunc={() => {
                            const pageIds = paginatedDocuments.map((doc: any) => doc.id);
                            const allPageSelected = pageIds.every((id: number) => selectedIds.includes(id));
                            setSelectedIds(allPageSelected ? selectedIds.filter((id) => !pageIds.includes(id)) : Array.from(new Set([...selectedIds, ...pageIds])));
                        }}
                        name="selectAllDocs"
                        value="selectAllDocs"
                        disabled={loading}
                    />
                ),
                sortable: false,
                visible: true,
                rowClassName: '!pr-2 w-[40px] min-w-[40px] max-w-[40px] sticky left-0 bg-inherit z-[1]',
            },
            { id: 'fileName', name: t('Orders.File_name'), sortable: false, visible: true, rowClassName: 'min-w-[240px] max-w-[240px]' },
            { id: 'legId', name: t('Orders.Leg_ID'), sortable: false, visible: true, rowClassName: 'min-w-[140px] max-w-[140px]' },
            { id: 'type', name: t('Common.Type'), sortable: false, visible: true, rowClassName: 'min-w-[155px] max-w-[155px]' },
            { id: 'dateUploaded', name: t('Orders.Date_uploaded'), sortable: false, visible: true, rowClassName: 'min-w-[160px] max-w-[160px]' },
            { id: 'uploadedBy', name: t('Orders.Uploaded_by'), sortable: false, visible: true, rowClassName: 'min-w-[150px] max-w-[150px]' },
            { id: 'actions', name: '', sortable: false, visible: true, rowClassName: 'min-w-[64px] w-[64px] max-w-[64px] sticky right-0 bg-inherit' },
        ],
        [t, paginatedDocuments, selectedIds, loading, setSelectedIds]
    );

    const handleViewDocument = (document: any) => {
        dispatch(setLegDocumentsState({ viewDocument: document }));
    };

    const handleDeleteDocument = (documentId: number) => {
        dispatch(setLegDocumentsState({ deleteDocumentId: documentId }));
    };

    const confirmDelete = () => {
        if (!deleteDocumentId) return;

        dispatch(setLegDocumentsState({ isDeleting: true }));
        deleteLegDocument(deleteDocumentId)
            .then(() => {
                dispatch(removeLegDocument(deleteDocumentId));
                WalToast.success(t('Orders.Document_deleted'));
            })
            .catch(() => {
                WalToast.error(t('Orders.Document_delete_failed'));
            })
            .finally(() => {
                dispatch(setLegDocumentsState({ isDeleting: false, deleteDocumentId: null }));
            });
    };

    // Reset to page 1 whenever the document list changes
    useEffect(() => {
        setCurrentPage(1);
    }, [filteredDocuments.length]);

    // Public share page: when the customer shares documents they are listed in the Documents widget
    // (cp-meeting-changes F3), so this per-leg sign-in teaser would contradict it; it stays otherwise.
    if (isReadOnly && Array.isArray((order as any)?.sharedDocuments) && (order as any).sharedDocuments.length > 0) {
        return null;
    }

    if (isReadOnly) {
        return (
            <CardCmp headerTitle={t('Orders.Leg_Documents')} cardClassName="overflow-hidden" headerParentClassName="lg:min-h-fit !rounded-t-lg" isCardContentWithoutSpacing={true}>
                <div className="relative w-full table-box-container overflow-hidden">
                    {/* Blurred dummy table */}
                    <div className="blur-sm pointer-events-none max-h-[192px] overflow-hidden select-none" aria-hidden="true">
                        <BulkActionBar selectedRows={[1, 2]} setSelectedRows={() => {}} allIds={[1, 2, 3, 4]} showOnlySelected={false} onShowOnlySelectedChange={() => {}} />
                        {/* overflow-hidden, not auto: a decorative table must not show a scrollbar under the overlay */}
                        <div className="overflow-hidden">
                            <TableCmp
                                tableRowHeight="xs"
                                headCells={headCells}
                                tableDataArr={DUMMY_DOCUMENTS}
                                TableRowCmp={LegDocumentsRow}
                                TableLoaderRowCmp={LegDocumentsLoaderRow}
                                isTableDataLoading={false}
                                tableBodyRowClass="last:border-b-0"
                                numberOfSkeletonRows={3}
                                tableRowCmpProps={{ isReadOnly: true }}
                            />
                        </div>
                        <CustomPagination recordsPerPage={5} totalRecords={DUMMY_DOCUMENTS.length} currentPage={1} handlePagination={() => {}} />
                    </div>

                    {/* Login overlay */}
                    <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 bg-white/60 backdrop-blur-[2px] z-10">
                        <NotFoundUI
                            icon={<Lock01 className="w-5 h-5 text-primary" />}
                            iconClassName="!bg-primary/10 !border-primary/20 !rounded-full !shadow-none !hidden"
                            title="Login to view documents"
                            desc="Sign in to access and download shipment documents"
                            containerClassName="!my-0 !mx-0"
                            descClass="!pb-0"
                            buttonText="Login to access"
                            buttonType="btn-outline-primary"
                            handleActionType={() => (window.location.href = ROUTES.LOGIN)}
                            isEmptyBackground={true}
                        />
                    </div>
                </div>
            </CardCmp>
        );
    }

    return (
        <>
            <CardCmp headerTitle={t('Orders.Leg_Documents')} cardClassName="overflow-hidden min-h-fit" headerParentClassName="!rounded-t-lg" isCardContentWithoutSpacing={true}>
                <div className="w-full table-box-container relative">
                    {!loading && filteredDocuments.length === 0 ? (
                        <NotFoundUI
                            title="No Documents found"
                            desc="There are no documents available for this leg."
                            iconClassName="!hidden"
                            iconSecondaryClassName="!hidden"
                            isFixedHeight={true}
                            iconSecondary={<File06 className="w-6 h-6 text-primary" />}
                            isEmptyBackground={true}
                        />
                    ) : (
                        <>
                            {bulkSelection.isOpen && (
                                <BulkActionBar
                                    selectedRows={selectedIds}
                                    setSelectedRows={setSelectedIds}
                                    allIds={allDocumentIds}
                                    showOnlySelected={showAllSelected}
                                    onShowOnlySelectedChange={setShowAllSelected}
                                />
                            )}
                            <div className="overflow-x-auto custom-scrollbar scrollbar-hide">
                                <TableCmp
                                    tableRowHeight="xs"
                                    headCells={headCells}
                                    tableDataArr={loading ? [] : paginatedDocuments}
                                    TableRowCmp={LegDocumentsRow}
                                    TableLoaderRowCmp={LegDocumentsLoaderRow}
                                    isTableDataLoading={loading}
                                    tableBodyRowClass="last:border-b-0 bg-white"
                                    numberOfSkeletonRows={3}
                                    isBulkBarOpen={bulkSelection.isOpen}
                                    isFirstColumnSticky
                                    isActionColumnSticky
                                    isTableRowClickableCondition={(row: any) => !!(row?.fileUrl && row?.file)}
                                    onRowClick={(row: any) => handleViewDocument(row)}
                                    tableRowCmpProps={{
                                        handleViewDocument,
                                        handleDeleteDocument,
                                        isReadOnly,
                                        selectedIds,
                                        onToggleSelect: handleToggleSelectRow,
                                    }}
                                />
                            </div>
                            <CustomPagination recordsPerPage={recordsPerPage} totalRecords={displayDocuments.length} currentPage={currentPage} handlePagination={setCurrentPage} />
                        </>
                    )}
                </div>
            </CardCmp>

            {/* View document modal */}
            {viewDocument && <DocumentPreviewModal document={viewDocument} handleClose={() => dispatch(setLegDocumentsState({ viewDocument: null }))} />}

            {/* Delete confirmation modal */}
            {deleteDocumentId !== null && (
                <DeleteModal
                    moduleTitle={t('Orders.Delete_Document')}
                    moduleDeleteModalContent={t('Orders.Delete_document_confirm')}
                    handleClose={(status: boolean) => () => {
                        if (status) {
                            confirmDelete();
                        } else {
                            dispatch(setLegDocumentsState({ deleteDocumentId: null }));
                        }
                    }}
                    dangerBtnText={t('Common.Delete')}
                    isDeleteLoading={isDeleting}
                />
            )}
        </>
    );
};

export default LegDocumentsSection;
